/** P3 신호 강도 bin + Strategy drift + 열린 포지션. */
import {
  OpenPositionsBlock,
  SignalStrengthBlock,
  StrategyDriftBlock,
} from '../../services/lossStructureApi';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const BIN_LABELS: Record<string, string> = {
  lt_25: '< 25%',
  '25_29': '25~29%',
  '30_34': '30~34%',
  '35_39': '35~39%',
  '40_44': '40~44%',
  '45_plus': '≥ 45%',
};

interface Props {
  signal: SignalStrengthBlock;
  drift: StrategyDriftBlock;
  open: OpenPositionsBlock;
}

export default function LossSignalDrift({ signal, drift, open }: Props) {
  return (
    <div className="loss-secondary-grid">
      <section className="loss-section">
        <h3>신호 강도 ↔ 결과</h3>
        <p className="loss-subnote">
          출처: <code>{signal.source}</code> · 매칭 실패 {signal.no_match_count}건
        </p>
        <table className="loss-table">
          <thead>
            <tr><th>구간</th><th>건수</th><th>승/패</th><th>합계 $</th></tr>
          </thead>
          <tbody>
            {signal.bins.map((b) => {
              const isLowSample = b.count > 0 && b.count < 5;
              return (
                <tr key={b.bin}>
                  <td>{BIN_LABELS[b.bin] || b.bin}{isLowSample && <span className="loss-sample-flag"> (표본 {b.count})</span>}</td>
                  <td>{b.count}</td>
                  <td>{b.wins} / {b.losses}</td>
                  <td className={toneClass(b.sum_amount)}>{money.format(b.sum_amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="loss-section">
        <h3>현재 열린 포지션 미실현</h3>
        <p className="loss-subnote">기준 리포트: {open.report_date || '데이터 없음'}</p>
        <table className="loss-table">
          <thead>
            <tr><th>종목</th><th>수량</th><th>미실현 $</th><th>%</th><th>보유 일</th></tr>
          </thead>
          <tbody>
            {open.positions.map((p) => (
              <tr key={p.symbol}>
                <td>{p.symbol}</td>
                <td>{p.qty ?? '-'}</td>
                <td className={toneClass(p.unrealized_pl ?? 0)}>
                  {p.unrealized_pl != null ? money.format(p.unrealized_pl) : '-'}
                </td>
                <td className={toneClass(p.unrealized_pl_percent ?? 0)}>
                  {p.unrealized_pl_percent != null ? `${p.unrealized_pl_percent.toFixed(2)}%` : '-'}
                </td>
                <td>{p.days_held != null ? `${p.days_held}` : '-'}</td>
              </tr>
            ))}
            {open.positions.length === 0 && (
              <tr><td colSpan={5} className="loss-empty">현재 열린 포지션 없음</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="loss-section loss-section-wide">
        <h3>Strategy Health Drift</h3>
        <p className="loss-subnote">{drift.note}</p>
        <table className="loss-table">
          <thead>
            <tr><th>전략</th><th>BUY votes</th><th>SELL votes</th><th>HOLD votes</th><th>합계</th></tr>
          </thead>
          <tbody>
            {drift.strategies.map((s) => (
              <tr key={s.strategy}>
                <td>{s.strategy}</td>
                <td className={s.buy_votes === 0 ? 'loss-cell-muted' : ''}>{s.buy_votes}</td>
                <td>{s.sell_votes}</td>
                <td>{s.hold_votes}</td>
                <td>{s.total}</td>
              </tr>
            ))}
            {drift.strategies.length === 0 && (
              <tr><td colSpan={5} className="loss-empty">AgentDecision 표본 없음</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function toneClass(value: number): string {
  if (value > 0) return 'loss-cell-good';
  if (value < 0) return 'loss-cell-bad';
  return '';
}
